import './ConsoleBind.css';
import React, { useState, useEffect, useRef, ReactNode, FormEvent } from 'react'
import { Form, Button, Spinner } from "react-bootstrap";
import QrCodeScannerModal from "../modal/qrCodeScannerModal/qrCodeScannerModal";
import { useTranslation } from "react-i18next";
import { logger } from '../../utils/logger';
import useDroneHubConnection from '../../hooks/DroneHubConnection';
import { HubBindIsOkMessage, HubBindIsOkSchema } from '../../asp/DroneHub';
import { APIRequestPost } from '../../utils/APIRequests';
import { BindModemResponseSchema, DroneBindModemRequestData } from '../../api/Drone';

interface ConsoleBindProps {
    // Возвращает true, если привязанный модем появился в списке.
    onBindOk?: (imei: string) => Promise<boolean>;
}

function ConsoleBind({ onBindOk }: ConsoleBindProps): ReactNode {
    const { t } = useTranslation(['modem']);

    const droneHub = useDroneHubConnection();

    const [pressedBindInfo, setPressedBindInfo] = useState<boolean>(false);
    const [modemNotOnlineInfo] = useState<ReactNode>(<></>);
    const [bindTime, setBindTime] = useState<number>(40);
    const [codeValue, setCodeValue] = useState<string>('');
    const [isQrCodeScannerModalOpen, setQrCodeScannerModalOpen] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>("");

    const timerRef = useRef<NodeJS.Timeout>(null);
    const onBindOkRef = useRef(onBindOk);
    const codeValueRef = useRef(codeValue);

    useEffect(() => {
        onBindOkRef.current = onBindOk;
    }, [onBindOk]);

    useEffect(() => {
        codeValueRef.current = codeValue;
    }, [codeValue]);

    const onChangeCodeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setCodeValue(event.target.value);
    };

    const onScanQrCodeClick = (): void => {
        setQrCodeScannerModalOpen(true);
    }

    const onDecode = (decodedText: string): void => {
        const tokens = decodedText.match("IMEI:([0-9]*)");
        if (!tokens || tokens.length < 2 || !tokens[1]) {
            console.warn("Failed to parse tokens", tokens);
            return;
        }
        setCodeValue(tokens[1]);
        setQrCodeScannerModalOpen(false);
    }

    // Привязка прошла успешно: обновляем список модемов без перезагрузки страницы,
    // чтобы не терять состояние консоли. IMEI очищаем только когда модем
    // действительно появился в списке, иначе оставляем его в поле.
    function bindIsOk(imei?: string): void {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setPressedBindInfo(false);
        setErrorMsg("");
        onBindOkRef.current?.(imei ?? codeValueRef.current).then((found) => {
            if (found) {
                setCodeValue("");
            }
            else {
                setErrorMsg('error');
            }
        });
    }

    useEffect(() => {
        const onHubBindIsOk = (message: HubBindIsOkMessage): void => {
            const result = HubBindIsOkSchema.safeParse(message);
            logger.debug("bind is ok. imei:" + message?.imei);
            bindIsOk(result.success ? result.data.imei : undefined);
        };
        droneHub.connection.on('BindIsOk', onHubBindIsOk);
        return () => {
            droneHub.connection.off('BindIsOk', onHubBindIsOk);
        };
    }, [droneHub.connection]);

    useEffect(() => {
        setErrorMsg("");
    }, [codeValue]);

    // таймер срабатывает по клику
    const startTimer = (): void => {
        timerRef.current = setTimeout(() => {
            setPressedBindInfo(false);
        }, bindTime * 1000);
    }

    useEffect(() => {
        logger.debug("bind mount");
        startTimer();
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            logger.debug("bind.js unmount");
        };
    }, []);

    function submitFormBindModem(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault()

        const data: DroneBindModemRequestData = {
            imei: codeValue
        };

        APIRequestPost('/drone/bindModem', {
            data: data,
            schema: BindModemResponseSchema,
            logSuccess: true
        }).then((result) => {
            if (typeof result.data !== "string") {
                if (result.data.state === "NotPressedBind") {
                    setPressedBindInfo(true);
                    logger.debug("bind time: " + result.data.bindTime);
                    if (result.data.bindTime) {
                        setBindTime(result.data.bindTime);
                    }
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }
                    startTimer();
                }
                else if (result.data.state === "Ok") {
                    bindIsOk(data.imei);
                }
            }
        }).catch((error) => {
            // IMEI в поле сохраняем, чтобы его не пришлось вводить заново.
            if (error?.response?.status === 400) {
                setErrorMsg(error.response.data);
            }
            else {
                logger.debug(error);
                setErrorMsg('error');
            }
        });
    }

    return (
        <div className='console-bind'>
            <div className='addition-white t-14 bind-label'>{t('adding_device')}</div>

            <Form onSubmit={submitFormBindModem}>
                <Form.Control
                    type="text"
                    placeholder="IMEI"
                    value={codeValue}
                    onChange={onChangeCodeInput}
                />

                {(pressedBindInfo === false) ? (
                    <div className='bind-buttons'>
                        <Button variant="primary" type="submit">
                            Bind
                        </Button>
                        <Button variant="light" onClick={onScanQrCodeClick}>
                            Scan QR-Code
                        </Button>
                        {errorMsg &&
                            <div className='bind-error-text'>
                                {t(errorMsg)}
                            </div>
                        }
                    </div>
                ) : (
                    <div className='bind-spinner-info'>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <div>{t('press_bind_button_1')}{bindTime}{t('press_bind_button_2')}</div>
                    </div>
                )}
                {modemNotOnlineInfo}
            </Form>
            <QrCodeScannerModal
                show={isQrCodeScannerModalOpen}
                onDecode={onDecode}
                onClose={() => setQrCodeScannerModalOpen(false)}
            />
        </div>
    )
}

export default ConsoleBind;